<template>
  <form
    id="rb-form"
    @submit.prevent="submit()"
  >
    <div>
      <h1 class="title">
        {{ config.visual.first_form_title }}
      </h1>
      <h2 class="subtitle">
        {{ config.visual.first_form_complementary }}
      </h2>
    </div>
    <RbSelect
      v-model="form.course"
      :config="{ key: 'key', title: 'title' }"
      :data="options.course.data"
      :loading="options.course.loading"
      label="Curso Ofertado"
      required
      primary
    />
    <RbSelect
      v-model="form.campus"
      :config="{ key: 'key', title: 'title' }"
      :data="options.campus.data"
      :loading="options.campus.loading"
      label="Local"
      primary
      required
      :disabled="!form.course"
    />
    <RbSelect
      v-model="form.period"
      :config="{ key: 'key', title: 'title' }"
      :data="options.period.data"
      :loading="options.period.loading"
      label="Turno"
      primary
      required
      :disabled="!form.campus"
    />
  </form>
</template>

<script lang="ts" setup>
import { onBeforeMount, reactive, watch } from 'vue';
import RbSelect from '../standards/RbSelect.vue';
import { simulation, getCourses, getCampus, getPeriods, getSimilar } from '@/api/Courses';
import { getLastSimulation } from '@/api/Person';
import { authenticate } from '@/lib/Auth';
import { appStore, loaderStore, proposalStore, snackbarStore, stepStore } from '@/store/app';;
import type { IConfig } from './interface/IConfig';
import type { ISimulation } from './interface/ISimulation';

const config = appStore().getConfig() as IConfig;
const form = reactive<{
  course?: any,
  campus?: any,
  period?: any
}>({});

interface IOption {
  data: Array<any>,
  loading: boolean,
}

const options = reactive<{
  course: IOption,
  campus: IOption,
  period: IOption
}>({
  course: {
    data: [],
    loading: false,
  },
  campus: {
    data: [],
    loading: false,
  },
  period: {
    data: [],
    loading: false,
  }
});

onBeforeMount(async () => {
  if(Boolean(appStore().global.course) && !proposalStore().getProposal().simulation_id) {
    await simulateWithSimilarCourse();
  }
  await initFormData();
});

async function simulateWithSimilarCourse() {
  try {
    loaderStore().setOpen(true);
    const similarCourse = await getSimilar({ 
      client: config.visual.client_id, 
      course: appStore().global.course, 
      campus: appStore().global.campus ?? ''
    });

    if(similarCourse) {
      await submit(similarCourse.course_id);
    }
  } catch (error) {
    loaderStore().setOpen(false);
    return false;
  }
}

async function initFormData() {
  loaderStore().setOpen(true);
  try {
    const simulation = await getLastSimulation() as ISimulation;
    if (simulation) {
      await setFormValue(simulation);
      loaderStore().setOpen(false);
    } else {
      loaderStore().setOpen(false);
      await loadCourses();
    }
    watch(() => form.course, handleCampus);
    watch(() => form.campus, handlePeriods);
  } catch (error) {
    snackbarStore().alert('Tivemos um problema, aguarde alguns segundos e tente novamente!');
  } finally {
    loaderStore().setOpen(false);
  }
}

async function setFormValue(simulation: ISimulation) {
  await loadWidgetCoursesData(simulation.course_offering?.name, simulation.course_offering?.campus_id);
  form.course = options.course.data.find(e => e.key == simulation.course_offering?.name);
  form.campus = options.campus.data.find(e => e.key == simulation.course_offering?.campus_id);
  form.period = options.period.data.find(e => e.key == simulation.course_offering?.period_id);
}

async function submit(courseId?: Number) {
  loaderStore().setOpen(true);
  try {
    await authenticate();
    const data = await simulation(courseId ?? form.period.course);
    if (data.id) {
      proposalStore().setSimulationId(data.id);
      stepStore().nextStep('simulation');
    }
  } catch (error) {
    snackbarStore().alert('Tivemos um problema, aguarde alguns segundos e tente novamente!');
  }
  finally {
    loaderStore().setOpen(false);
  }
}

async function loadWidgetCoursesData(course, campus) {
  return Promise.all([
    loadCourses(),
    loadCampus(course),
    loadPeriods(course, campus)
  ]);
}

async function handleCampus() {
  if (form.course) {
    await loadCampus();
  } else {
    options.campus.data = [];
  }
  form.campus = options.campus.data.find(e => e.key == form.campus?.key);
}

async function handlePeriods() {
  if (form.campus) {
    await loadPeriods();
  } else {
    options.period.data = [];
  }
  form.period = options.period.data.find(e => e.key == form.period?.key);
}

async function loadCourses() {
  options.course.loading = true;
  options.course.data = await getCourses(config.visual.client_id);
  options.course.loading = false;
}

async function loadCampus(course = undefined) {
  options.campus.loading = true;
  options.campus.data = await getCampus(config.visual.client_id, course ?? form.course.key);
  options.campus.loading = false;
}

async function loadPeriods(course = undefined, campus = undefined) {
  options.period.loading = true;
  options.period.data = await getPeriods(config.visual.client_id, course ?? form.course.key, campus ?? form.campus.key);
  options.period.loading = false;
}
</script>

<style lang="scss" scoped>
form#rb-form {
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  position: relative;
  gap: 2rem;

  h1,
  h2 {
    font-size: 1rem;
    line-height: 1rem;
    margin: 0;
    padding: 0;
  }

  h1.title {
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0em;
    text-align: left;
    color: black;
  }

  h2.subtitle {
    font-size: 0.85rem;
    font-weight: 400;
    line-height: 23px;
    letter-spacing: 0.025em;
    text-align: left;

    color: rgba(77, 77, 77, 1);
  }

  &>button {
    align-self: center;
    margin-bottom: 5px;
  }
}
</style>
